/**
 * Daily Matters — Meta pixel "Lead" for the VIP sign-up.
 *
 * The Facebook & Instagram app's pixel only sends shop events (PageView, ViewContent, AddToCart,
 * InitiateCheckout, AddPaymentInfo, Purchase, Search), so a sign-up through the DM sign-up form never
 * reaches Meta as a conversion. This module sends one standard Lead event per sign-up, so ads can
 * optimise on and report sign-ups. It listens to the dm:subscribed event that dm-waitlist.js dispatches
 * once per sign-up, normally on the success page Shopify renders after the form's native post.
 *
 * Consent: the Lead is only sent when Shopify's Customer Privacy API says marketing is allowed. A visitor
 * who signs up before answering the cookie banner gets the Lead as soon as they accept; one who declines
 * never does. Without the API (blocked, not loaded) nothing is sent.
 *
 * The pixel is initialised here, on the page itself, with automatic events switched off, so it adds
 * nothing but the Lead. The app's own pixel keeps running separately in its sandbox and shares the _fbp
 * cookie. The e-mail address and first name of the sign-up go along as advanced matching; fbevents.js
 * hashes them in the browser before sending.
 *
 * Ad click: a Meta ad link carries ?fbclid=…, the click Meta uses to tie a Lead to one ad. The Lead usually
 * fires on the success page after Shopify's post, where that parameter is gone, so once marketing is allowed
 * the click is kept as the _fbc cookie (90 days, Meta's own format fb.1.<ms>.<fbclid>), which fbevents.js
 * reads for every event it sends. Without marketing consent nothing is stored.
 *
 * Server copy: with the section setting "Conversions API relay URL" (data-meta-capi-url) set, the same Lead,
 * with the same event ID, also goes to the Cloudflare Worker in workers/meta-capi, which passes it on to Meta's
 * Conversions API. Meta keeps one of the two, and still gets the sign-up when the browser copy is blocked.
 * Same consent rule; e-mail and name leave the browser SHA-256 hashed.
 *
 * The pixel ID comes from the sign-up section setting "Meta pixel ID" (data-meta-pixel-id on the form).
 * Leave it empty to switch this module off.
 */
(() => {
  // The merchant's own test sign-ups in the theme editor are not conversions.
  if (window.Shopify && window.Shopify.designMode) return;

  const form = document.querySelector('form[data-dm-signup-form][data-meta-pixel-id]');
  const pixelId = form ? form.dataset.metaPixelId.trim() : '';
  if (!pixelId) return;
  const capiUrl = (form.dataset.metaCapiUrl || '').trim();

  // One Lead per sign-up: a reload of the posted page (?customer_posted=true) must not count twice.
  const SENT_KEY = 'dm-meta-lead-sent';

  const session = {
    get(key) {
      try {
        return sessionStorage.getItem(key) || '';
      } catch (error) {
        return '';
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch (error) {
        /* private mode — ignore */
      }
    },
  };

  const readCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  };

  /**
   * E-mail address and first name of the sign-up, from the record dm-waitlist.js keeps between the submit
   * and the success page (localStorage first, sessionStorage as fallback). Read when the sign-up is
   * announced: dm-waitlist.js drops them from the record after the survey.
   */
  const signupContact = () => {
    for (const area of ['localStorage', 'sessionStorage']) {
      try {
        const record = JSON.parse(window[area].getItem('dm-signup') || 'null');
        if (record && record.email) {
          return {
            email: String(record.email).toLowerCase(),
            firstName: String(record.firstName || '').toLowerCase(),
          };
        }
      } catch (error) {
        /* unavailable or unreadable — try the next one */
      }
    }
    return { email: '', firstName: '' };
  };

  const marketingAllowed = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    try {
      return Boolean(privacy && typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed());
    } catch (error) {
      return false;
    }
  };

  /** Meta's standard base code, without the PageView: the app's pixel already sends that. */
  const loadPixel = () => {
    if (typeof window.fbq === 'function') return window.fbq;
    const fbq = function () {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
      else fbq.queue.push(arguments);
    };
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    return fbq;
  };

  let pending = null;

  /** SHA-256 in hex, the form the Conversions API expects for e-mail and name. */
  const sha256 = async (value) => {
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  /**
   * The same Lead, with the same event ID, to the Conversions API relay (workers/meta-capi), so Meta still gets
   * it when the browser copy is blocked or cut off, and counts it once when both arrive. E-mail and name leave
   * the browser hashed only. Sent as text/plain with keepalive: no CORS preflight, and it survives a page change.
   */
  const sendServerCopy = async (lead) => {
    if (!capiUrl || !lead.email || !window.crypto || !crypto.subtle) return;
    try {
      const body = {
        event_id: lead.eventId,
        event_source_url: window.location.href,
        em: await sha256(lead.email.trim()),
        fn: lead.firstName ? await sha256(lead.firstName.trim()) : '',
        fbc: readCookie('_fbc'),
        fbp: readCookie('_fbp'),
        custom_data: {
          content_name: 'VIP-lijst Energy Matters',
          entry_point: lead.entryPoint || 'direct',
          flavour: lead.flavour || '',
          page_version: (form && form.dataset.pageVersion) || '',
        },
      };
      await fetch(capiUrl, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(body),
      });
    } catch (error) {
      console.error('[dm-meta]', error);
    }
  };

  const sendLead = (lead) => {
    if (session.get(SENT_KEY)) return;
    try {
      const fbq = loadPixel();
      // No automatic button-click or microdata events from this copy of the pixel.
      fbq('set', 'autoConfig', false, pixelId);
      // Taken when the sign-up was announced, sent only now that the visitor allowed marketing cookies.
      const matching = {};
      if (lead.email) matching.em = lead.email;
      if (lead.firstName) matching.fn = lead.firstName;
      fbq('init', pixelId, matching);
      fbq(
        'track',
        'Lead',
        {
          content_name: 'VIP-lijst Energy Matters',
          entry_point: lead.entryPoint || 'direct',
          flavour: lead.flavour || '',
          page_version: (form && form.dataset.pageVersion) || '',
        },
        { eventID: lead.eventId }
      );
      session.set(SENT_KEY, lead.eventId);
      sendServerCopy(lead);
    } catch (error) {
      // Measurement must never break the page that carries the sign-up.
      console.error('[dm-meta]', error);
    }
  };

  /** Runs the callback once Shopify's Customer Privacy API is there; it can load after this module. */
  const whenPrivacyReady = (callback) => {
    const shopify = window.Shopify;
    if (shopify && shopify.customerPrivacy) {
      callback();
    } else if (shopify && typeof shopify.loadFeatures === 'function') {
      shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) callback();
      });
    }
  };

  const flush = () => {
    if (pending && marketingAllowed()) {
      sendLead(pending);
      pending = null;
    }
  };

  document.addEventListener('dm:subscribed', (event) => {
    const { entryPoint, flavour } = event.detail || {};
    pending = {
      entryPoint,
      flavour,
      ...signupContact(),
      eventId: `dm-lead-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    };
    whenPrivacyReady(flush);
  });

  /* Ad click (fbclid → _fbc) --------------------------------------------- */
  const FBC_MAX_AGE_S = 90 * 24 * 60 * 60;
  const clickId = (new URLSearchParams(window.location.search).get('fbclid') || '').trim();

  /** Stores the click of this page view, unless _fbc already holds it (the app's pixel may have set it). */
  const keepClick = () => {
    if (!clickId || !marketingAllowed()) return;
    if (readCookie('_fbc').endsWith(`.${clickId}`)) return;
    try {
      document.cookie = `_fbc=fb.1.${Date.now()}.${encodeURIComponent(clickId)}; path=/; max-age=${FBC_MAX_AGE_S}; SameSite=Lax; Secure`;
    } catch (error) {
      /* cookies blocked — ignore */
    }
  };

  if (clickId) whenPrivacyReady(keepClick);

  // Shopify fires this when the visitor answers the cookie banner.
  document.addEventListener('visitorConsentCollected', () => {
    keepClick();
    flush();
  });
})();
